import { Fragment, useState, type FormEvent } from 'react';
import {
  updateMailcomAccountCredentials,
  type UpdateMailcomAccountCredentialsInput,
  type WireProviderAccount,
} from '../../api/endpoints.js';
import { useApiMutation } from '../../api/hooks.js';
import { Banner, Button, Modal } from '../../ui/index.js';

type Mode = 'bundle' | 'separate' | 'ref';

export interface UpdateCredentialModalProps {
  readonly providerId: string;
  readonly open: boolean;
  readonly account: WireProviderAccount | null;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export function UpdateCredentialModal({
  providerId,
  open,
  account,
  onClose,
  onSuccess,
}: UpdateCredentialModalProps): JSX.Element | null {
  if (!open || !account) return null;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <>
          {`Credential ${account.hasCredential ? 'Güncelle' : 'Ekle'} — `}
          {handleWithBreak(account.accountHandle)}
        </>
      }
      width={620}
      testId="update-credential-modal"
    >
      <Body
        providerId={providerId}
        accountId={account.id}
        accountHandle={account.accountHandle}
        hasCredential={account.hasCredential}
        onSuccess={() => {
          onSuccess();
          onClose();
        }}
        onCancel={onClose}
      />
    </Modal>
  );
}

/**
 * The handle with soft line-break opportunities before "@" and inside the
 * local part (after ".", "-", "_"), so a long address in the dialog title
 * wraps at a word boundary rather than at an arbitrary letter. Text content
 * (and the accessible name) unchanged.
 */
function handleWithBreak(handle: string): JSX.Element {
  const at = handle.lastIndexOf('@');
  if (at <= 0) return <>{handle}</>;
  // Same hints as the parent cards (ParentMailsTable HandleText): after each
  // ".", "-" or "_" of the local part, and before "@domain".
  const local = handle.slice(0, at).split(/(?<=[.\-_])/).filter((p) => p !== '');
  return (
    <>
      {local.map((part, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {part}
        </Fragment>
      ))}
      <wbr />
      {handle.slice(at)}
    </>
  );
}

function Body({
  providerId,
  accountId,
  accountHandle,
  hasCredential,
  onSuccess,
  onCancel,
}: {
  readonly providerId: string;
  readonly accountId: string;
  readonly accountHandle: string;
  readonly hasCredential: boolean;
  readonly onSuccess: () => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const [mode, setMode] = useState<Mode>('bundle');
  const [bundle, setBundle] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [parentRefreshToken, setParentRefreshToken] = useState('');
  const [credRef, setCredRef] = useState('');

  const mutation = useApiMutation((o, input: UpdateMailcomAccountCredentialsInput) =>
    updateMailcomAccountCredentials(o, input, providerId),
  );

  function reset(): void {
    setBundle('');
    setParentEmail('');
    setParentPassword('');
    setParentRefreshToken('');
    setCredRef('');
  }

  async function onSubmit(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    let payload: UpdateMailcomAccountCredentialsInput = { accountId };
    if (mode === 'bundle') {
      const t = bundle.trim();
      if (!t) return;
      payload = { ...payload, parentBundle: t };
    } else if (mode === 'separate') {
      const e = parentEmail.trim();
      const p = parentPassword;
      const r = parentRefreshToken.trim();
      if (!e || !p || !r) return;
      payload = { ...payload, parentEmail: e, parentPassword: p, parentRefreshToken: r };
    } else {
      const ref = credRef.trim();
      if (!ref) return;
      payload = { ...payload, credentialRef: ref };
    }
    const result = await mutation.run(payload);
    if (result.kind === 'ok') {
      reset();
      onSuccess();
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="mailcom-update-credential-form"
      aria-label={`mail.com parent credential ${hasCredential ? 'güncelle' : 'ekle'}`}
    >
      <p className="t-tertiary t-small" style={{ margin: '0 0 14px' }}>
        Bu form mevcut parent hesabın <strong>credential_ref</strong> sütununu yeni bir{' '}
        <code>vault:&lt;id&gt;</code> kayıt ile değiştirir. Hiçbir terminal script çalıştırmanıza gerek yok. Form
        gönderildikten sonra ham bilgi (şifre, refresh token) hiçbir API yanıtında, log'da veya outbox event'inde
        geri dönmez.
      </p>

      <div className="tab-bar mcp-cred-modes" role="tablist">
        <button type="button" className={mode === 'bundle' ? 'active' : ''} onClick={() => setMode('bundle')}>
          Bundle
        </button>
        <button type="button" className={mode === 'separate' ? 'active' : ''} onClick={() => setMode('separate')}>
          Ayrı alanlar
        </button>
        <button type="button" className={mode === 'ref' ? 'active' : ''} onClick={() => setMode('ref')}>
          Mevcut env: ref
        </button>
      </div>

      {mode === 'bundle' ? (
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Bundle</label>
          <textarea
            value={bundle}
            onChange={(e) => setBundle(e.target.value)}
            placeholder="email:şifre:refreshToken"
            aria-label="parent bundle"
            rows={3}
            style={{ width: '100%' }}
            required
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      ) : null}

      {mode === 'separate' ? (
        <div className="form-grid">
          <label htmlFor={`update-email-${accountId}`}>E-posta</label>
          <input
            id={`update-email-${accountId}`}
            type="email"
            value={parentEmail}
            onChange={(e) => setParentEmail(e.target.value)}
            placeholder="parent@mail.com"
            aria-label="parent email"
            autoComplete="off"
            required
          />
          <label htmlFor={`update-password-${accountId}`}>Şifre</label>
          <input
            id={`update-password-${accountId}`}
            type="password"
            value={parentPassword}
            onChange={(e) => setParentPassword(e.target.value)}
            aria-label="parent password"
            autoComplete="new-password"
            required
          />
          <label htmlFor={`update-refresh-${accountId}`}>OAuth refresh token</label>
          <input
            id={`update-refresh-${accountId}`}
            type="password"
            value={parentRefreshToken}
            onChange={(e) => setParentRefreshToken(e.target.value)}
            aria-label="parent refresh token"
            autoComplete="off"
            required
          />
        </div>
      ) : null}

      {mode === 'ref' ? (
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Credential ref</label>
          <input
            type="text"
            value={credRef}
            onChange={(e) => setCredRef(e.target.value)}
            placeholder="env:USER_VAR:PASS_VAR[:TOKEN_VAR]"
            aria-label="credential ref"
            style={{ width: '100%' }}
            autoComplete="off"
          />
        </div>
      ) : null}

      <div className="mcp-form-actions mcp-sheet-actions" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
        <Button type="button" variant="secondary" onClick={onCancel}>İptal</Button>
        <Button type="submit" variant="primary" loading={mutation.state.kind === 'running'}>
          {hasCredential ? 'Credential güncelle' : 'Credential kaydet'}
        </Button>
      </div>

      {mutation.state.kind === 'error' ? (
        <div style={{ marginTop: 8 }}>
          <Banner tone="danger">Hata: {mutation.state.reason}</Banner>
        </div>
      ) : null}
      {mutation.state.kind === 'ok' ? (
        <div style={{ marginTop: 8 }}>
          <Banner tone="success">
            Credential kaydedildi. credentialRefStatus=
            {String((mutation.state.data as { credentialRefStatus?: string }).credentialRefStatus ?? 'absent')}{' '}
            refreshTokenPresent=
            {String(Boolean((mutation.state.data as { refreshTokenPresent?: boolean }).refreshTokenPresent))}
          </Banner>
        </div>
      ) : null}

      {/* Reference the handle to keep label consistent across renders */}
      <span hidden>{accountHandle}</span>
    </form>
  );
}
